import { Injectable } from '@angular/core';
import { getBucket } from 'secchiojs';

interface KeyRange {
	lower?: number;
	lowerOpen?: boolean;
	upper?: number;
	upperOpen?: boolean;
}

interface Index {
	indexName?: string;
	prop?: string;
	order?: 'asc' | 'desc';
}

@Injectable()
export class CacheService {
	private static instance: CacheService;
	private stores: any;
	private persistTimeOut: NodeJS.Timeout;

	private defaultIndexes = {
		default: {
			date: 'timestamp',
			startDate: 'date',
			createdAt: 'cattimestamp',
			updatedAt: 'uattimestamp',
		},
	};

	get: () => any;
	set: (values: any) => void;
	delete: () => void;

	static getInstance(): CacheService {
		if (!CacheService.instance) {
			CacheService.instance = new CacheService();
		}
		return CacheService.instance;
	}

	constructor() {
		[this.get, this.set, this.delete] = getBucket('zenklubDb', {
			version: 7,
			defaultValue: null,
		});
		const storage = this.get();
		if (storage) {
			this.stores = storage;
		} else {
			this.stores = {};
			this.persistStores();
		}
	}

	deleteStore(storeName: string): Promise<void> {
		return new Promise((resolve) => {
			delete this.stores[storeName];
			this.persistStores();
			resolve();
		});
	}

	clearDatabase(): Promise<void> {
		return new Promise((resolve) => {
			this.stores = {};
			this.persistStores();
			resolve();
		});
	}

	getStore(storeName: string): any {
		if (!this.stores[storeName]) {
			this.stores[storeName] = {
				keyMap: {},
				data: [],
				indexes: this.defaultIndexes[storeName]
					? this.defaultIndexes[storeName]
					: this.defaultIndexes['default'],
			};
		}
		return this.stores[storeName];
	}

	persistStores(): void {
		if (this.persistTimeOut) {
			clearTimeout(this.persistTimeOut);
		}
		this.persistTimeOut = setTimeout(() => {
			try {
				this.set(this.stores);
			} catch {
				this.clearDatabase();
				this.set(this.stores);
			}
			this.persistTimeOut = null;
		}, 50);
	}

	validateRange(
		object: any,
		keyRange?: KeyRange,
		keyFilterName?: string
	): boolean {
		let isValid = false;
		if (keyRange.lower && object[keyFilterName]) {
			if (keyRange.lowerOpen) {
				isValid = object[keyFilterName] > keyRange.lower;
			} else {
				isValid = object[keyFilterName] >= keyRange.lower;
			}
			if (!isValid) {
				return false;
			}
		}
		if (keyRange.upper && object[keyFilterName]) {
			if (keyRange.upperOpen) {
				isValid = object[keyFilterName] < keyRange.upper;
			} else {
				isValid = object[keyFilterName] <= keyRange.upper;
			}
			if (!isValid) {
				return false;
			}
		}
		return isValid;
	}

	syncCount(storeName: string): number {
		const store = this.getStore(storeName);
		return store.data.length;
	}

	getAll(
		storeName: string,
		keyRange?: KeyRange,
		indexDetails?: Index
	): Promise<any> {
		const store = this.getStore(storeName);
		return new Promise<any>((resolve) => {
			let result: any;

			if (keyRange && indexDetails) {
				let keyFilterName: any;
				if (store.indexes[indexDetails.indexName]) {
					keyFilterName = store.indexes[indexDetails.indexName];
				} else {
					keyFilterName = indexDetails.indexName;
				}

				result = store.data.filter((item: any) => {
					return !item
						? false
						: this.validateRange(item, keyRange, keyFilterName);
				});
			} else {
				result = store.data.filter((item: any) => {
					return !!item;
				});
			}

			if (indexDetails && indexDetails.indexName) {
				let keySortName: any;
				if (store.indexes[indexDetails.indexName]) {
					keySortName = store.indexes[indexDetails.indexName];
				} else {
					keySortName = indexDetails.indexName;
				}

				if (indexDetails.prop !== undefined) {
					result.sort((a: any, b: any) => {
						const ad = a[keySortName]
							? new Date(a[keySortName][indexDetails.prop])
							: new Date(null);
						const bd = b[keySortName]
							? new Date(b[keySortName][indexDetails.prop])
							: new Date(null);
						if (ad < bd) {
							return -1;
						}
						if (ad > bd) {
							return 1;
						}
						return 0;
					});
				} else {
					result.sort((a: any, b: any) => {
						if (a[keySortName] < b[keySortName]) {
							return -1;
						}
						if (a[keySortName] > b[keySortName]) {
							return 1;
						}
						return 0;
					});
				}

				if (indexDetails.order && indexDetails.order.toLowerCase() === 'desc') {
					result.reverse();
				}
			}

			resolve(result);
		});
	}

	getByKey(storeName: string, key: any): Promise<any> {
		const store = this.getStore(storeName);
		return new Promise<any>((resolve) => {
			resolve(Object.assign({}, store.data[store.keyMap[key]]));
		});
	}

	remove(storeName: string, key: any): Promise<void> {
		const store = this.getStore(storeName);

		return new Promise<void>((resolve) => {
			if (store.keyMap[key] !== undefined) {
				delete store.data[store.keyMap[key]];
				delete store.keyMap[key];
				this.persistStores();
			}
			resolve();
		});
	}

	update(storeName: string, value: any): any {
		const store = this.getStore(storeName);
		const currentKey = value?.id?.toString();
		if (store.keyMap[currentKey] !== undefined) {
			// Object already exists will replace it
			let storeValue = { ...value };
			if (storeName === 'events') {
				const currentValue = store.data[store.keyMap[currentKey]] || {};
				storeValue = Object.assign({}, currentValue, value);
			}
			store.data[store.keyMap[currentKey]] = storeValue;
		} else {
			store.data.push({ ...value });
			store.keyMap[currentKey] = store.data.length - 1;
		}

		this.persistStores();
		return { ...value };
	}

	cleanStorage(storeName: string) {
		this.stores[storeName] = {
			keyMap: {},
			data: [],
		};
		return this.stores[storeName];
	}

	deleteValueFromStore(
		storeName: string,
		currentKey: string | number
	): Promise<boolean> {
		const store = this.getStore(storeName);
		return new Promise<any>((resolve) => {
			let res = false;
			if (store.keyMap[currentKey] !== undefined) {
				// actually clear the entry on the offline database
				store.data[store.keyMap[currentKey]] = undefined;
				res = true;
			}
			this.persistStores();
			resolve(res);
		});
	}

	async openCursor(
		storeName: string,
		cursorCallback: (evt: any) => void,
		keyRange?: KeyRange,
		indexDetails?: Index
	): Promise<any> {
		const results = await this.getAll(storeName, keyRange, indexDetails);
		function nextItem(index: number) {
			const event = { target: { result: undefined } };
			if (!results[index]) {
				return cursorCallback(event);
			}
			event.target.result = {
				value: results[index],
				continue: () => {
					nextItem(index + 1);
				},
			};
			return cursorCallback(event);
		}
		return nextItem(0);
	}
}
